import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, View } from 'react-native';
import { useLazyQuery, gql } from '@apollo/client';
import { Controller } from 'react-hook-form';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartShopping,
  faShop,
  faStore,
} from '@fortawesome/free-solid-svg-icons';

import { Container, Input, Results } from 'app/src/components/Autocomplete';
import { StyledTextInput } from 'app/src/elements/inputs';
import { Text } from 'app/src/styles';
import * as colors from 'app/src/styles/colors';

import { Field, FieldLabel, errorsFor } from './inputs';

const SEARCH_ENTITIES = gql`
  query NoUserLogEntitySearch(
    $term: String!
    $preferredTypes: [String!]
  ) {
    search(
      type: "Entity"
      term: $term
      preferredTypes: $preferredTypes
    ) {
      ... on Buyer {
        id
        name
      }
      ... on Seller {
        id
        name
      }
      ... on Vendor {
        id
        name
      }
    }
  }
`;

const ResultContainer = styled(Pressable)`
  flex-direction: row;
  column-gap: 10px;
  margin: 5px;
`;

const Name = styled(View)`
`;

const NameText = styled(Text)`
  font-weight: 600;
`;

const TYPES = {
  Buyer: { icon: faCartShopping, color: 'navy' },
  Seller: { icon: faShop, color: colors.elGreen },
  Vendor: { icon: faStore, color: 'orange' },
};

const Result = ({ result, handleSelect }) => {
  const options = TYPES[result.__typename];

  return (
    <ResultContainer onPress={() => handleSelect(result)}>
      <FontAwesomeIcon {...options} />
      <Name>
        <NameText>{result.name}</NameText>
      </Name>
    </ResultContainer>
  );
};

const EntityAutocomplete = ({ field, preferredTypes, setValue }) => {
  const [searching, setSearching] = useState(false);
  const [offsets, setOffsets] = useState({ top: 0, left: 0 });
  const [results, setResults] = useState([]);
  const [selectedName, setSelectedName] = useState(field.value);

  const [searchEntities] = useLazyQuery(SEARCH_ENTITIES, {
    onCompleted: ({ search }) => setResults(search)
  });

  const ref = useCallback(element => {
    if (element) {
      setOffsets(element.getBoundingClientRect());
    }
  }, []);

  const handleChange = value => {
    setSelectedName(value);
    field.onChange(value);

    if (!searching) { setSearching(true); }

    if (value.length > 0) {
      searchEntities({ variables: { preferredTypes, term: value } });
    }

    setValue('entityType', null);
    setValue('entityId', null);
  };

  const handleKeyPress = e => {
    if ('Enter' === e.nativeEvent.key) {
      setSearching(false);
    }
  };

  const handleBlur = () => {
    window.setTimeout(() => setSearching(false), 200);
    field.onBlur();
  };

  const handleSelect = result => {
    setSelectedName(result.name);
    field.onChange(result.name);
    setValue('entityType', result.__typename);
    setValue('entityId', result.id);
    setSearching(false);
  };

  const styles = {
    maxWidth: 500,
    top: offsets.top + 12,
    left: offsets.left,
  };

  return (
    <Container style={{ marginBottom: 0, minWidth: 200, flex: 1 }}>
      <div ref={ref} />
      <Input
        {...field}
        value={selectedName || field.value}
        style={{ marginLeft: 0 }}
        onChangeText={handleChange}
        onKeyPress={handleKeyPress}
        onBlur={handleBlur}
      />
      {searching && field.value.length >= 1 && results.length > 0 && (
        createPortal(
          <Results style={styles}>
            {results.map(result => (
              <Result
                key={`${result.__typename}-${result.id}`}
                result={result}
                handleSelect={handleSelect}
              />
            ))}
          </Results>, document.body
        )
      )}
    </Container>
  );
};

const EntityNameInput = props => {
  const {
    name, label, hint, control, rules, errors, inline,
    preferredTypes, setValue,
  } = props;

  const [printDifferentName, setPrintDifferentName] = useState(false)

  const updatePrintedName = ({ enabled, value }) => {
    if (enabled) {
      if (!printDifferentName) {
        setPrintDifferentName(true);
      }

      if (value) {
        setValue('name', value);
      }
    } else {
      if (printDifferentName) {
        setPrintDifferentName(false);
        setValue('name', null);
      }
    }
  };

  return (
    <Field inline={inline} errors={errorsFor(errors, name)}>
      {!inline && (
        <FieldLabel
          name={name}
          label={label}
          hint={hint}
          errors={errors}
        />
      )}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
              <EntityAutocomplete
                field={field}
                preferredTypes={preferredTypes}
                setValue={setValue}
              />
              <input
                type="checkbox"
                checked={printDifferentName}
                onChange={() => {
                  updatePrintedName({ enabled: !printDifferentName })
                }}
              />
              <Text>
                Print Different Name
              </Text>
            </View>

            {printDifferentName && (
              <>
                <Text>Printed Name</Text>
                <StyledTextInput
                  value={field.value}
                  onChangeText={value => {
                    updatePrintedName({ enabled: true, value })
                  }}
                />
              </>
            )}
          </>
        )}
      />
    </Field>
  );
};

export default EntityNameInput;
